import { JSONSchema7 } from "json-schema";
import { JsonValue, JsonObject } from "@/v2/shared/types";
import { useCallback } from "react";

function dedupeSchemas(schemas: JSONSchema7[]): JSONSchema7[] {
  const seen = new Set<string>();
  const out: JSONSchema7[] = [];
  for (const s of schemas) {
    const key = JSON.stringify(s);
    if (!seen.has(key)) {
      seen.add(key);
      out.push(s);
    }
  }
  return out;
}

function buildJsonSchema(value: JsonValue): JSONSchema7 {
  if (value === null) return { type: "null" };

  const t = typeof value;
  if (t === "string") return { type: "string" };
  if (t === "boolean") return { type: "boolean" };
  if (t === "number") {
    return Number.isInteger(value) ? { type: "integer" } : { type: "number" };
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return { type: "array", items: {} };
    }
    const itemSchemas = value.map(buildJsonSchema);
    const deduped = dedupeSchemas(itemSchemas);

    return deduped.length === 1
      ? { type: "array", items: deduped[0] }
      : { type: "array", items: { anyOf: deduped } };
  }

  const obj = value as JsonObject;
  const properties: Record<string, JSONSchema7> = {};
  const required: string[] = [];

  for (const [k, v] of Object.entries(obj)) {
    properties[k] = buildJsonSchema(v);
    required.push(k);
  }

  return {
    type: "object",
    properties,
    required: required.length ? required : undefined,
  };
}

export function useBuildJsonSchema() {
  return useCallback((value: JsonValue) => {
    return buildJsonSchema(value);
  }, []);
}
